import data from './subprocessors.json';

/**
 * Phase 6 Pass 3 iter 243 — subprocessor source-of-truth loader.
 *
 * Per `pass-3-trust-center.md` § Audit gates → Architecture:
 * "subprocessor list single source of truth (JSON); RSS regenerates
 * on change."
 *
 * The JSON file is the wire-of-truth that drives:
 *
 *   1. The `/trust/subprocessors` table page (iter 243 — THIS iter).
 *   2. The `/trust/subprocessors.rss` build-time RSS feed (iter 244).
 *   3. A summary mention on the `/trust` hub.
 *
 * **Schema (locked, Architect hat)**:
 *
 *   - `id`: stable slug used as the React key and as the i18n key
 *     segment (`subprocessors.row.<id>.*`). Locale-independent.
 *   - `name`: legal entity name as it appears in the customer DPA.
 *     **Verbatim in every locale** — a proper noun, never translated.
 *   - `purposeKey`: i18n key resolving to the one-line description of
 *     the data-processing purpose (GDPR Art. 28 § 3(a)
 *     instruction-of-controller clause). The prose localizes; vendor
 *     and product proper nouns inside it stay verbatim.
 *   - `dataKeys`: ordered list of i18n keys, each resolving to one
 *     data-category line (GDPR Art. 28 § 3(a)).
 *   - `locationKey`: i18n key resolving to the country / region where
 *     the processor's instances of customer data primarily reside.
 *     Drives the transfer-mechanism column. Country names localize per
 *     locale convention.
 *   - `role`: 'processor' for sub-processors that touch customer
 *     content; in GDPR terms 'sub-processor' = 'processor' relative
 *     to Syncanix, so we use 'processor'.
 *   - `transferKey`: i18n key resolving to the GDPR Art. 44-49
 *     mechanism prose. Statute references (e.g. "Standard Contractual
 *     Clauses (Module Two)") localize as prose.
 *   - `website`: canonical homepage; clickable in the table.
 *
 * **Type-only schema (Lead Dev hat, locked)**: types declared
 * statically; runtime validation lives in `subprocessors.test.ts`
 * + JSON-shape regression tests. We don't add a zod runtime
 * dependency to apps/marketing for one bundled file — the shape
 * is reviewable in PR and tested at every CI run, so the runtime
 * cost is unjustified.
 *
 * **Three-layer contract preserved**: this loader is pure
 * presentational data — no runtime fetch. Compile-time JSON import
 * is fine because the file is small (~2 KB), changes infrequently,
 * and is intentionally bundled with the marketing site so a CDN
 * outage of the JSON path doesn't blank the page.
 */

export interface Subprocessor {
  readonly id: string;
  readonly name: string;
  readonly purposeKey: string;
  readonly dataKeys: readonly string[];
  readonly locationKey: string;
  readonly role: 'processor';
  readonly transferKey: string;
  readonly website: string;
  /**
   * Repo-relative path to the code that actually integrates this vendor (B13 —
   * G2 anti-fiction contract). Every row MUST name a real integration path, and
   * `subprocessors.test.ts` asserts the file exists AND references the vendor
   * OUTSIDE comments — so a never-wired vendor (the removed Sentry/Postmark
   * fiction class, whose only references were future-tense comments) can never
   * be listed as a sub-processor again.
   */
  readonly integrationPath: string;
}

export interface SubprocessorDocument {
  readonly lastUpdated: string;
  readonly policyDays: number;
  readonly subprocessors: readonly Subprocessor[];
}

export const subprocessorDocument: SubprocessorDocument = data as SubprocessorDocument;

export const subprocessors: readonly Subprocessor[] = subprocessorDocument.subprocessors;
